import { useState, type ChangeEvent, type FormEvent } from 'react';
import { validarSenha } from '@agencia/shared';
import { useAuth } from '@/auth/AuthContext';
import { Alert, AuthLayout, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

/** Primeiro acesso: cria o administrador usando o código que aparece nos logs da Render. */
export function SetupPage() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({ codigo: '', nome: '', email: '', senha: '', confirmacao: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const senhaErros = form.senha ? validarSenha(form.senha, form.email) : [];
  const confirmacaoErro = form.confirmacao && form.confirmacao !== form.senha ? 'As senhas não conferem.' : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (senhaErros.length || confirmacaoErro) return;
    setLoading(true);
    try {
      await api.post('/setup', { codigo: form.codigo, nome: form.nome, email: form.email, senha: form.senha });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Administrador criado" subtitle="Agora entre com o seu e-mail e senha para configurar a verificação em duas etapas.">
        <Button onClick={() => void refresh()}>Ir para o login</Button>
      </AuthLayout>
    );
  }

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  return (
    <AuthLayout
      step="PRIMEIRO ACESSO"
      title="Criar o administrador"
      subtitle="Esta tela só aparece enquanto o sistema não tem nenhum usuário."
    >
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        {error && <Alert>{error}</Alert>}
        <Field
          label="Código de configuração"
          hint="Aparece nos registros (Logs) do serviço na Render, na linha “CÓDIGO DE CONFIGURAÇÃO INICIAL”."
          value={form.codigo}
          onChange={set('codigo')}
          autoComplete="off"
          placeholder="XXXX-XXXX-XXXX"
          required
        />
        <Field label="Nome completo" value={form.nome} onChange={set('nome')} autoComplete="name" required />
        <Field label="E-mail" type="email" value={form.email} onChange={set('email')} autoComplete="username" required />
        <Field
          label="Senha"
          type="password"
          value={form.senha}
          onChange={set('senha')}
          autoComplete="new-password"
          hint="Mínimo de 10 caracteres, com letras e números."
          error={senhaErros[0]}
          required
        />
        <Field
          label="Confirme a senha"
          type="password"
          value={form.confirmacao}
          onChange={set('confirmacao')}
          autoComplete="new-password"
          error={confirmacaoErro}
          required
        />
        <Button type="submit" loading={loading}>
          Criar administrador
        </Button>
      </form>
    </AuthLayout>
  );
}
